import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { VerificationAgencyContactsService } from '../../../Component/Api/VerificationAgency/VerificationAgencyContacts/VerificationAgencyContactsService';
import { ColumnSortOption } from '../../../Component/Model/Dto/ColumnSortOption';
import { VerificationAgencyContacts } from '../../../Component/Model/Dto/VerificationAgency/VerificationAgencyContacts';
import { EntriesPerPage, IBulkActions } from '../../../Component/Model/Table/Table';
import { VerificationCompanyAtom } from '../../../State/VerificationCompany.Atom';
import { ContactsModal } from './ContactsModal/ContactsModal';
import { VerificationAgencyContactTable } from './ContactTable';
import { VAContactTableMapper } from './ContactTableMapper';
import { ConfirmationDialog } from '../../../Component/Common/Components/ConfirmationDialog';

export const Contacts = () => {
    const verificationCompany = useRecoilValue(VerificationCompanyAtom);
    const [showContactModal, setShowContactModal] = React.useState<boolean>(false);
    const [filteredItemsCount, setFilteredItemsCount] = useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [vaContactData, setVaContactData] = useState<VerificationAgencyContacts.ContactResult[]>([])
    const [contactId, setContactId] = useState<number>();
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState<boolean>(false);

    const [pageNumber, setPageNumber] = useState<number>(1);
    const [searchValue, setSearchValue] = useState<string>("");
    const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });

    const confirmDeleteMessage = "Are you sure you want to delete this contact?";

    const contactSearchRequest: VerificationAgencyContacts.VerificationAgencyContactSearchRequest = {
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue!,
        searchValue: searchValue,
        vaClientId: verificationCompany.vaClientId,
        columnSortOption: columnSortOptions
    }

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }
    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }
    const searchValueChanged = (value: string) => {
        setSearchValue(value);
        setPageNumber(1);
    }
    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const fetchVaContacts = async () => {
        setIsLoading(true);
        try {
            const contactData = await VerificationAgencyContactsService.VAClientApplicationSearch(contactSearchRequest);
            setVaContactData(contactData.data.results);
            setTotalItemsCount(contactData.data.totalItemsCount);
            setFilteredItemsCount(contactData.data.filteredItemsCount);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        fetchVaContacts();
    }, [entriesPerPage, pageNumber, searchValue, columnSortOptions])

    const bulkActions: IBulkActions[] = [
        {
            label: 'Add Contact',
            action: () => setShowContactModal(true)
        }
    ]

    const showDeleteConfirmation = (contactId: number) => {
        setContactId(contactId);
        setShowDeleteConfirmationDialog(true);
    }

    const handleCloseConfirmation = () => {
        setShowDeleteConfirmationDialog(false);
        setContactId(undefined);
    }

    const deleteContact = async () => {
        if(contactId){
            try {
                await VerificationAgencyContactsService.DeleteEntry(contactId);
                toast.success("Contact Deleted")
                fetchVaContacts();
            } catch (error) {
                console.log(error);
            } finally {
                handleCloseConfirmation();
            }
        }
    }

    const updateContact = (contactId: number) => {
        setContactId(contactId);
        setShowContactModal(true);
    }

    const closeContactModal = () => {
        setShowContactModal(false);
        setContactId(undefined);
        fetchVaContacts();
    }


    const ContactsTableMapper = VAContactTableMapper(vaContactData, showDeleteConfirmation, updateContact)
    return (
        <>
            <VerificationAgencyContactTable
                bulkActions={bulkActions}
                entriesPerPage={entriesPerPage}
                entriesPerPageChanged={entriesPerPageChanged}
                filteredItemsCount={filteredItemsCount}
                isLoading={isLoading}
                pageNumberChanged={pageNumberChanged}
                searchValueChanged={searchValueChanged}
                results={ContactsTableMapper!}
                totalItemsCount={totalItemsCount}
                pageNumber={contactSearchRequest.pageNumber}
                columnSortingChanged={columnSortingChanged}
            />
            {showContactModal && <ContactsModal showContactsModal={showContactModal} closeContactsModal={closeContactModal} contactId={contactId}/>}

            {showDeleteConfirmationDialog && 
                <ConfirmationDialog
                    showDialog={showDeleteConfirmationDialog}
                    handleAccept={deleteContact}
                    handleCancel={handleCloseConfirmation}
                    heading={"Confirm Delete"}
                    message={confirmDeleteMessage}
                />
            }
        </>
    )
}

